import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpenCheck,
  Building2,
  Check,
  CirclePlay,
  Clock3,
  Route,
  ShieldCheck,
  Sparkles,
  Target,
  UsersRound,
} from 'lucide-react';
import { useApp } from '@/app/AppProvider';
import { MODULES } from '@/exam';
import { bankSize } from '@/exam';
import { euros, precioIndividualVigente } from '@/premium';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

const EXAM_DATE = new Date('2026-10-02T00:00:00+02:00');

function daysLeft() {
  return Math.ceil((EXAM_DATE.getTime() - Date.now()) / 86_400_000);
}

export function HomePage() {
  const { config, me, premium } = useApp();
  const sizes = bankSize();
  const remainingDays = daysLeft();

  return (
    <>
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_78%_12%,color-mix(in_oklch,var(--primary)_12%,transparent),transparent_34%),linear-gradient(to_bottom,var(--background),color-mix(in_oklch,var(--muted)_55%,var(--background)))]" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 sm:py-20 lg:grid-cols-[1.08fr_.92fr] lg:py-24">
          <div>
            <Badge variant="secondary" className="mb-5 h-8 px-3 text-sm">
              <Clock3 className="size-4" /> {remainingDays > 0 ? `${remainingDays} días para la obligatoriedad` : 'Permiso obligatorio desde el 2 de octubre de 2026'}
            </Badge>
            <h1 className="max-w-3xl text-balance text-4xl font-semibold leading-[1.07] tracking-[-0.035em] sm:text-5xl lg:text-6xl">
              No necesitas aprobar el conjunto. Necesitas aprobar{' '}
              <span className="text-primary">cada módulo.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground sm:text-xl">
              Simulacros reales, estudio por bloques y un panel que te dice exactamente qué módulo te dejaría no apto.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button render={<Link to="/estudio" />} size="lg" className="shadow-raised">
                Practicar gratis · 187 oficiales <ArrowRight />
              </Button>
              <Button render={<Link to={me?.usuario ? '/panel' : '/precios'} />} size="lg" variant="outline">
                {me?.usuario ? 'Ver mi progreso' : 'Ver el acceso completo'}
              </Button>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
              {[
                '187 preguntas oficiales gratis',
                'Sin tarjeta para empezar',
                '60 minutos como la prueba real',
                // El precio no aparecía hasta /precios: quien llega de un anuncio
                // se iba sin saber si esto costaba 5 € o 200 €. Sale de la config,
                // así que la promo caduca sola y nunca anuncia un descuento muerto.
                ...(config.pagoActivo
                  ? [`Acceso completo ${euros(precioIndividualVigente(config))}, pago único`]
                  : []),
              ].map((item) => (
                <span key={item} className="inline-flex items-center gap-1.5"><Check className="size-4 text-success" />{item}</span>
              ))}
            </div>
          </div>

          <Card className="border-0 bg-card/95 shadow-raised">
            <CardHeader className="border-b">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardDescription>Diagnóstico de preparación</CardDescription>
                  <CardTitle className="mt-1 text-2xl">¿Aprobarías hoy?</CardTitle>
                </div>
                <span className="grid size-11 place-items-center rounded-xl bg-primary/10 text-primary"><Target /></span>
              </div>
            </CardHeader>
            <CardContent className="grid gap-5 pt-1">
              {[72, 58, 64, 41].map((value, index) => (
                <div key={MODULES[index].id}>
                  <div className="mb-2 flex items-center justify-between gap-4 text-sm">
                    <span className="font-medium">Módulo {MODULES[index].id} · {MODULES[index].short}</span>
                    <span className={value < 50 ? 'font-semibold text-destructive' : 'font-semibold text-success'}>{value}%</span>
                  </div>
                  <div className="relative">
                    <Progress value={value} className="[&_[data-slot=progress-track]]:h-2.5" />
                    <span className="absolute inset-y-0 left-1/2 w-px bg-foreground/40" aria-hidden />
                  </div>
                </div>
              ))}
              <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4">
                <p className="font-semibold text-destructive">Hoy quedarías no apto</p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">El módulo IV está por debajo del corte del 50%. Ese es el siguiente bloque que deberías trabajar.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="border-b bg-card/70" aria-labelledby="platforms-title">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          <div className="grid items-center gap-6 lg:grid-cols-[1fr_auto]">
            <div>
              <p id="platforms-title" className="font-semibold">Preparado para quienes mueven Madrid cada día</p>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">Formación independiente para conductores y flotas de las principales plataformas VTC.</p>
            </div>
            <div className="grid grid-cols-3 items-center gap-3 sm:gap-5" aria-label="Plataformas VTC">
              <div className="grid h-14 min-w-0 place-items-center rounded-xl border bg-background px-5">
                <img src="/brands/uber.svg" alt="Uber" className="h-8 w-20 dark:invert" />
              </div>
              <div className="grid h-14 min-w-0 place-items-center rounded-xl border bg-background px-5">
                <img src="/brands/bolt.svg" alt="Bolt" className="h-8 w-auto dark:brightness-0 dark:invert" />
              </div>
              <div className="grid h-14 min-w-0 place-items-center rounded-xl border bg-background px-5">
                <img src="/brands/cabify.svg" alt="Cabify" className="h-7 w-auto" />
              </div>
            </div>
          </div>
          <p className="mt-4 text-[0.68rem] leading-5 text-muted-foreground">
            Uber, Bolt y Cabify son marcas de sus respectivos titulares. Permiso VTC es una plataforma independiente y no está afiliada, patrocinada ni avalada por estas compañías.
          </p>
        </div>
      </section>

      <section className="border-b bg-card/65">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-14 sm:px-6 sm:py-16 lg:grid-cols-[.9fr_1.1fr]">
          <a
            href="/video-examen-vtc-madrid-modulo-4"
            className="group relative block overflow-hidden rounded-2xl border bg-slate-950 shadow-raised focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Ver el vídeo gratuito sobre el marco jurídico VTC de Madrid"
          >
            <img
              src="/videos/modulo-4-marco-juridico-poster.jpg"
              alt="Portada del vídeo del módulo IV sobre el marco jurídico VTC de Madrid"
              className="aspect-video w-full object-cover opacity-90 transition-transform duration-300 group-hover:scale-[1.02]"
            />
            <span className="absolute inset-0 grid place-items-center bg-slate-950/18">
              <span className="grid size-16 place-items-center rounded-full bg-background/95 text-primary shadow-raised">
                <CirclePlay className="size-9" />
              </span>
            </span>
          </a>
          <div>
            <Badge variant="secondary">Nuevo · Vídeo gratuito</Badge>
            <h2 className="mt-4 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
              Entiende el marco jurídico VTC antes de memorizar respuestas
            </h2>
            <p className="mt-4 text-lg leading-8 text-muted-foreground">
              Te explicamos autorizaciones, contratación previa, registro, hoja de ruta, usuarios e infracciones a partir de fuentes oficiales.
            </p>
            <Button render={<a href="/video-examen-vtc-madrid-modulo-4" />} size="lg" className="mt-6">
              Ver vídeo del módulo IV <ArrowRight />
            </Button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: Clock3, value: '60 min', label: 'para 60 preguntas' },
            { icon: Route, value: '4 módulos', label: 'todos deben superar el 50%' },
            { icon: ShieldCheck, value: '0 penalización', label: 'los fallos no restan' },
          ].map(({ icon: Icon, value, label }) => (
            <Card key={value} className="shadow-soft">
              <CardContent className="flex items-center gap-4">
                <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground"><Icon /></span>
                <div><p className="text-lg font-semibold">{value}</p><p className="text-sm text-muted-foreground">{label}</p></div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-20 max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">Preparación enfocada</p>
          <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">Trabaja el módulo que te puede suspender, no el que ya dominas</h2>
          <p className="mt-4 text-lg leading-8 text-muted-foreground">Cada bloque tiene un temario y un corte propios. Practica en sesiones de diez o reproduce el examen completo.</p>
        </div>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {MODULES.map((module) => (
            <Card key={module.id} className="group shadow-soft transition-transform hover:-translate-y-0.5">
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <Badge variant="outline">Módulo {module.id}</Badge>
                  <span className="text-sm font-medium text-muted-foreground">{module.count} en el examen</span>
                </div>
                <CardTitle className="mt-3 text-xl">{module.name}</CardTitle>
                <CardDescription className="leading-6">{module.topics}</CardDescription>
              </CardHeader>
              <CardContent className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{sizes[module.id]} disponibles ahora</span>
                <Button render={<Link to={`/estudio?modulo=${module.id}`} />} variant="ghost">Practicar <ArrowRight /></Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="border-y bg-muted/45">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">Un producto para cada realidad</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">Desde un conductor hasta una academia completa</h2>
          </div>
          <div className="mt-9 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { to: '/conductores', icon: Target, title: 'Conductores', text: 'Simulacros y estudio breve desde el móvil.' },
              { to: '/autonomos', icon: Sparkles, title: 'Autónomos', text: 'Preparación flexible sin horarios ni cuotas.' },
              { to: '/flotas', icon: Building2, title: 'Flotas', text: 'Invitaciones, seguimiento consentido y riesgo.' },
              { to: '/academias', icon: UsersRound, title: 'Academias', text: 'Banco estructurado y seguimiento de grupos.' },
            ].map(({ to, icon: Icon, title, text }) => (
              <Link key={to} to={to} className="rounded-2xl focus-visible:ring-2 focus-visible:ring-ring">
                <Card className="h-full transition-colors hover:bg-accent/45">
                  <CardHeader><Icon className="size-6 text-primary" /><CardTitle className="mt-4">{title}</CardTitle><CardDescription className="leading-6">{text}</CardDescription></CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <Card className="overflow-hidden border-primary/20 bg-primary text-primary-foreground shadow-raised">
          <CardContent className="grid items-center gap-8 py-4 md:grid-cols-[1fr_auto] md:p-8">
            <div>
              <Badge className="bg-primary-foreground/12 text-primary-foreground">Pago único · sin suscripción</Badge>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight">Entiende por qué fallas, no memorices letras</h2>
              <p className="mt-3 max-w-2xl leading-7 text-primary-foreground/78">
                {config.premiumDisponible} preguntas de práctica y las 187 explicaciones razonadas desde {euros(config.precioIndividual)}.
              </p>
            </div>
            <Button render={<Link to="/precios" />} size="lg" variant="secondary">
              {premium ? 'Ver lo que incluye' : 'Comparar planes'} <ArrowRight />
            </Button>
          </CardContent>
        </Card>
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-8 pt-10 text-center sm:px-6">
        <BookOpenCheck className="mx-auto size-9 text-primary" />
        <h2 className="mt-5 text-3xl font-semibold tracking-tight">Empieza sabiendo dónde estás</h2>
        <p className="mx-auto mt-3 max-w-2xl text-lg leading-8 text-muted-foreground">Las 187 preguntas que publica la Comunidad, con su respuesta correcta, gratis y sin cuenta. Practica por módulos hasta que ninguno te sorprenda.</p>
        <Button render={<Link to="/estudio" />} size="lg" className="mt-7">Practicar ahora <ArrowRight /></Button>
      </section>
    </>
  );
}
